import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLeadership, createLeader, updateLeader, deleteLeader } from '../services/api';
import './AdminLeadership.css';

const EMPTY_FORM = { name: '', role: '', bio: '', photo_url: '', order: '' };

export default function AdminLeadership() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => getLeadership().then(setMembers).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleChange = (e) => {
    setError('');
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setForm({
      name: member.name,
      role: member.role,
      bio: member.bio || '',
      photo_url: member.photo_url || '',
      order: member.order ?? '',
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      setError('Le nom et le titre sont obligatoires.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      role: form.role.trim(),
      bio: form.bio.trim(),
      photo_url: form.photo_url.trim(),
      order: form.order !== '' ? parseInt(form.order, 10) : 0,
    };
    try {
      if (editingId) {
        await updateLeader(editingId, payload);
      } else {
        await createLeader(payload);
      }
      await load();
      cancelEdit();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteLeader(id);
      setConfirmDelete(null);
      await load();
    } catch {
      setError('Erreur lors de la suppression.');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="container">
          <Link to="/gestion-ecclesiale" className="admin-breadcrumb">← Tableau de bord</Link>
          <h1>⚙ Gestion de l'Équipe de Direction</h1>
          <p>Ajoutez, modifiez ou supprimez les membres de l'équipe affichés sur la page À Propos.</p>
        </div>
      </div>

      <div className="container admin-body">

        {/* ── Member List ─────────────────────────────────────────────── */}
        <section className="admin-section">
          <h2>Membres actuels ({members.length})</h2>
          {members.length === 0 ? (
            <p className="admin-empty">Aucun membre pour le moment. Ajoutez-en un ci-dessous.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Ordre</th>
                    <th>Photo</th>
                    <th>Nom</th>
                    <th>Titre</th>
                    <th>Biographie</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className={editingId === m.id ? 'row-editing' : ''}>
                      <td className="td-order">{m.order}</td>
                      <td className="td-thumb">
                        {m.photo_url ? (
                          <img src={m.photo_url} alt={m.name} className="table-thumb" />
                        ) : (
                          <div className="table-thumb-placeholder">
                            {m.name.split(' ').slice(-2).map((n) => n[0]).join('')}
                          </div>
                        )}
                      </td>
                      <td className="td-name">{m.name}</td>
                      <td><span className="role-badge">{m.role}</span></td>
                      <td className="td-bio">{m.bio || <em className="no-bio">—</em>}</td>
                      <td className="td-actions">
                        <button
                          className="btn-admin btn-edit"
                          onClick={() => startEdit(m)}
                        >
                          ✏ Modifier
                        </button>
                        {confirmDelete === m.id ? (
                          <span className="confirm-row">
                            <span>Confirmer?</span>
                            <button className="btn-admin btn-confirm-yes" onClick={() => handleDelete(m.id)}>Oui</button>
                            <button className="btn-admin btn-confirm-no" onClick={() => setConfirmDelete(null)}>Non</button>
                          </span>
                        ) : (
                          <button
                            className="btn-admin btn-delete"
                            onClick={() => setConfirmDelete(m.id)}
                          >
                            🗑 Supprimer
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Add / Edit Form ──────────────────────────────────────────── */}
        <section className="admin-section admin-form-section">
          <h2>{editingId ? '✏ Modifier le membre' : '+ Ajouter un nouveau membre'}</h2>
          <form className="admin-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nom complet <span className="required">*</span></label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Ex: Pasteur Jean-Baptiste Pierre"
                />
              </div>
              <div className="form-group">
                <label htmlFor="role">Titre / Rôle <span className="required">*</span></label>
                <input
                  id="role"
                  name="role"
                  type="text"
                  value={form.role}
                  onChange={handleChange}
                  placeholder="Ex: Pasteur Principal"
                />
              </div>
              <div className="form-group form-group--sm">
                <label htmlFor="order">Ordre d'affichage</label>
                <input
                  id="order"
                  name="order"
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={handleChange}
                  placeholder="1"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="bio">Biographie</label>
              <textarea
                id="bio"
                name="bio"
                value={form.bio}
                onChange={handleChange}
                rows={4}
                placeholder="Décrivez brièvement ce membre de l'équipe..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="photo_url">URL de la photo</label>
              <input
                id="photo_url"
                name="photo_url"
                type="url"
                value={form.photo_url}
                onChange={handleChange}
                placeholder="https://images.pexels.com/..."
              />
              <p className="field-hint">
                Collez une URL directe vers une image (Pexels, votre hébergement, etc.).
                La photo sera affichée en cercle sur la page À Propos.
              </p>
            </div>
            {form.photo_url && (
              <div className="photo-preview">
                <span className="photo-preview__label">Aperçu</span>
                <img
                  src={form.photo_url}
                  alt="Aperçu"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              </div>
            )}
            {error && <p className="admin-error">{error}</p>}
            <div className="form-actions">
              <button type="submit" className="btn-admin btn-save" disabled={saving}>
                {saving ? 'Enregistrement...' : editingId ? '💾 Enregistrer les modifications' : '➕ Ajouter le membre'}
              </button>
              {editingId && (
                <button type="button" className="btn-admin btn-cancel" onClick={cancelEdit}>
                  Annuler
                </button>
              )}
            </div>
          </form>
        </section>

      </div>
    </div>
  );
}
